import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  styled,
} from "@mui/material";
import { translations } from "../translations/common";

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
}

const ErrorDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogTitle-root": {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
  whiteSpace: "pre-wrap",
  fontFamily: "monospace",
  margin: theme.spacing(2, 0),
}));

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
    this.handleClose = this.handleClose.bind(this);
    this.resetError = this.resetError.bind(this);
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({
      error,
      errorInfo,
    });
    
    // You can log the error to an error reporting service here
    console.error("Error caught by boundary:", error, errorInfo);
  }

  resetError(): void {
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
    });
  }

  handleClose(): void {
    this.resetError();
  }

  render(): React.ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback: FallbackComponent } = this.props;

    if (!hasError) {
      return children;
    }

    if (FallbackComponent) {
      return <FallbackComponent error={error!} resetError={this.resetError} />;
    }

    return (
      <ErrorDialog open={hasError} onClose={this.handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{translations.error.title}</DialogTitle>
        <DialogContent>
          <ErrorMessage variant="body1">
            {error?.message || translations.error.generic}
          </ErrorMessage>
          {process.env.NODE_ENV === 'development' && error?.stack && (
            <ErrorMessage variant="body2" color="textSecondary">
              {error.stack}
            </ErrorMessage>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleClose} color="primary">
            {translations.error.close}
          </Button>
          <Button onClick={this.resetError} color="primary" variant="contained">
            {translations.error.retry}
          </Button>
        </DialogActions>
      </ErrorDialog>
    );
  }
}

export default ErrorBoundary;
